import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

// Embeds that start out as a picture: while the iframe loads in the
// background the image is shown in its place, then the live iframe takes the
// image's spot. Keyed by iframe src -> image src (both as written in the post).
const IFRAME_PLACEHOLDERS = {
    "/static/klotski.html": "/images/klotski.png",
};

// Whether ordinary iframes should load. Passed via context (not baked into the
// renderers) so toggling it - e.g. expanding a preview - doesn't remount the
// already-running swapped embed.
const EmbedsContext = createContext(true);

function PlainIframe(props) {
    const embeds = useContext(EmbedsContext);
    if (!embeds) {
        return null;
    }
    return <iframe title={props.title || props.src} {...props} />;
}

// If an embed never says it's ready (see postMessage in klotski.html), swap
// anyway this long after its page has loaded.
const READY_FALLBACK_MS = 4000;

// Width/height written on the post's <iframe src="..."> tag, so the swapped-in
// iframe keeps the size the post asked for.
function iframeAttributes(content, src) {
    const tag = (content.match(/<iframe\b[^>]*>/gi) || [])
        .find(t => t.includes(`"${src}"`) || t.includes(`'${src}'`));
    const attr = name => {
        const m = tag && tag.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, "i"));
        return m ? m[1] : undefined;
    };
    return { width: attr("width") || "100%", height: attr("height") || "800px" };
}

function SwappedIframe({ src, placeholder, alt, width, height }) {
    const [ready, setReady] = useState(false);
    const frameRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        const onMessage = (event) => {
            if (frameRef.current && event.source === frameRef.current.contentWindow
                && event.data && event.data.type === "embed-ready") {
                setReady(true);
            }
        };
        window.addEventListener("message", onMessage);
        return () => {
            window.removeEventListener("message", onMessage);
            clearTimeout(timerRef.current);
        };
    }, []);

    const onLoad = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setReady(true), READY_FALLBACK_MS);
    };

    return (
        <div className={`iframe-swap${ready ? " ready" : ""}`}>
            { !ready && <img src={placeholder} alt={alt || ""} className="iframe-swap-placeholder" /> }
            <iframe
                ref={frameRef}
                src={src}
                width={width}
                height={height}
                title={alt || src}
                onLoad={onLoad}
                className="iframe-swap-frame"
                aria-hidden={ready ? undefined : true}
                tabIndex={ready ? undefined : -1}
            />
        </div>
    );
}

// Post markdown, rendered the same way everywhere (post page, /words preview).
// With embeds={false} ordinary iframes aren't loaded; picture-swapped embeds
// (IFRAME_PLACEHOLDERS) always are, so they also come alive in the preview.
function ArticleMarkdown({ content, embeds = true }) {
    content = content || "";

    // Memoised on content only: if these renderer functions changed identity,
    // React would remount the embed (reloading the iframe / restarting the sim)
    // whenever the parent re-renders or the preview is expanded.
    const components = useMemo(() => {
        // Only swap when this post actually has both the iframe and its picture.
        const swaps = Object.entries(IFRAME_PLACEHOLDERS)
            .filter(([frame, image]) => content.includes(frame) && content.includes(image));
        const swapByImage = new Map(swaps.map(([frame, image]) => [image, frame]));
        const swappedFrames = new Set(swaps.map(([frame]) => frame));

        return {
            iframe: ({ node, ...props }) => {
                if (swappedFrames.has(props.src)) {
                    return null;
                }
                return <PlainIframe {...props} />;
            },
            img: ({ node, ...props }) => {
                const frame = swapByImage.get(props.src);
                if (frame) {
                    return <SwappedIframe src={frame} placeholder={props.src} alt={props.alt} {...iframeAttributes(content, frame)} />;
                }
                return <img alt="" {...props} />;
            },
        };
    }, [content]);

    return (
        <EmbedsContext.Provider value={embeds}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeRaw, rehypeKatex]}
                components={components}
            >{content}</ReactMarkdown>
        </EmbedsContext.Provider>
    );
}

export default ArticleMarkdown;
